package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.join_invite_invalid
import evoshift.composeapp.generated.resources.Res
import dev.gitlive.firebase.FirebaseException
import com.evoapps.evoshift.data.remote.CloudFunctionsException
import com.evoapps.evoshift.ui.util.UiText
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.usecase.AcceptInviteUseCase
import com.evoapps.evoshift.domain.usecase.ValidateInviteUseCase
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.firstOrNull
import kotlinx.coroutines.launch

sealed class JoinTeamState {
    object Idle : JoinTeamState()
    object Loading : JoinTeamState()
    data class InviteDetailsLoaded(
        val workplaceName: String,
        val inviterName: String,
        val isUserLoggedIn: Boolean
    ) : JoinTeamState()
    data class Error(val message: UiText) : JoinTeamState()
    object Success : JoinTeamState()
}

class JoinTeamViewModel(
    private val validateInviteUseCase: ValidateInviteUseCase,
    private val acceptInviteUseCase: AcceptInviteUseCase,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    /** SH-239: after joining, the dashboard offers "Recommend EVoShift" once. */
    private val recommendPrompt: RecommendPromptStore? = null
) : ViewModel() {

    private val _state = MutableStateFlow<JoinTeamState>(JoinTeamState.Idle)
    val state: StateFlow<JoinTeamState> = _state.asStateFlow()

    fun validateInvite(code: String) {
        if (code.isBlank()) return

        _state.value = JoinTeamState.Loading
        viewModelScope.launch {
            val user = getCurrentUserUseCase().firstOrNull()
            val result = validateInviteUseCase(code)
            
            _state.value = result.fold(
                onSuccess = { invite ->
                    JoinTeamState.InviteDetailsLoaded(
                        workplaceName = invite.workplaceName,
                        inviterName = invite.inviterName,
                        isUserLoggedIn = user != null
                    )
                },
                onFailure = { error ->
                    JoinTeamState.Error(error.toInviteUiText())
                }
            )
        }
    }

    fun acceptInvite(code: String) {
        if (code.isBlank()) return

        _state.value = JoinTeamState.Loading
        viewModelScope.launch {
            val result = acceptInviteUseCase(code)
            
            _state.value = result.fold(
                onSuccess = {
                    getCurrentUserUseCase().firstOrNull()?.uid?.let { recommendPrompt?.markPending(it) }
                    JoinTeamState.Success
                },
                onFailure = { error ->
                    JoinTeamState.Error(error.toInviteUiText())
                }
            )
        }
    }
}

/** Invitation failures: an unknown, used or expired code reads the same to the person joining. */
internal fun Throwable.toInviteUiText(): UiText = when (this) {
    is CloudFunctionsException ->
        if (code == "not-found" || code == "failed-precondition") UiText.Resource(Res.string.join_invite_invalid)
        else toUiText()
    is FirebaseException -> toUiText()
    else -> UiText.Resource(Res.string.join_invite_invalid)
}
